import React from "react";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import WorkoutHighlights, {
  useWorkoutHighlightsData,
} from "../card/WorkoutHighlights";
import ClientDetailsPrograms from "./ClientDetailsPrograms";
import { ClientFormsCard } from "../client-forms/ClientFormsCard";
import { ClientStepsCard } from "../client-steps/ClientStepsCard";
import { ClientNutritionCard } from "../client-nutrition-targets/ClientNutritionCard";
import { ClientGoalsCard } from "../card/ClientGoalsCard";
import { ClientBodyWeightCard } from "../client-body-weight/ClientBodyWeightCard";
import { ClientMeasurementsCard } from "../card/ClientMeasurementsCard";
import { ClientProgressPhotosCard } from "../card/ClientProgressPhotosCard";
import ClientWorkouts from "../client-workouts/ClientWorkouts";
import {
  CLIENT_PROFILE_DEFAULT_POSITIONS,
  ProfileCardPosition,
  ProfileCardType,
  withSpacers,
} from "../settings/coach-client-profile/utils";
import ClientExercises from "../client-workouts/ClientExercises";
import { BriefClientInfo } from "@growth-machine-llc/stridist-api-client";
import { Grid2, useMediaQuery, useTheme } from "@mui/material";
import { UserInfo } from "../../hooks/useCurrentUser";
import { PreviewType, usePreview } from "../../hooks/usePreview";

export interface ProfileCardConfig {
  component: React.ReactElement;
  condition?: boolean;
}

interface ProfileCardsProps {
  client: BriefClientInfo | UserInfo;
  profilePositions?: ProfileCardPosition[] | string;
  isPreview?: boolean;
  isFirst?: boolean;
}

export const ProfileCards = (props: ProfileCardsProps) => {
  const { client, profilePositions } = props;
  const {
    nutritionsEnabled,
    clientProfilePositions: workspaceClientProfilePositions,
  } = useCurrentWorkspace();

  const { hasWorkouts } = useWorkoutHighlightsData(client.id);

  const [preview] = usePreview();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreenPreview =
    preview === PreviewType.MOBILE || preview === PreviewType.TABLET;

  const collapsable = smDown || isSmallScreenPreview;
  const workspacePositions =
    workspaceClientProfilePositions &&
    JSON.parse(workspaceClientProfilePositions);
  const rawPositions: ProfileCardPosition[] = profilePositions?.length
    ? profilePositions
    : workspacePositions?.length
      ? workspacePositions
      : CLIENT_PROFILE_DEFAULT_POSITIONS;

  const normalizedPositions = React.useMemo(
    () => withSpacers(rawPositions, 2),
    [rawPositions],
  );

  const collapsableTypes = [
    ProfileCardType.FORMS,
    ProfileCardType.STEPS,
    ProfileCardType.NUTRITION,
    ProfileCardType.GOALS,
    ProfileCardType.BODY_WEIGHT,
    ProfileCardType.MEASUREMENTS,
    ProfileCardType.WORKOUTS,
    ProfileCardType.EXERCISES,
  ];

  const components: Partial<Record<ProfileCardType, ProfileCardConfig>> = {
    [ProfileCardType.WORKOUTS_HIGHLIGHTS]: {
      component: <WorkoutHighlights clientId={client.id} />,
    },
    [ProfileCardType.PROGRAMS]: {
      component: <ClientDetailsPrograms clientId={client.id} />,
    },
    [ProfileCardType.FORMS]: {
      component: (
        <ClientFormsCard
          clientId={client.id}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.STEPS]: {
      component: (
        <ClientStepsCard
          client={{ id: client.id, units: client.units }}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.NUTRITION]: {
      component: (
        <ClientNutritionCard
          client={{
            clientId: client.id,
            username: client.username,
            terraConnected: client.terraConnected,
          }}
          collapsable
          isPreview={isSmallScreenPreview}
        />
      ),
      condition: nutritionsEnabled,
    },
    [ProfileCardType.GOALS]: {
      component: (
        <ClientGoalsCard
          clientId={client.id}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.BODY_WEIGHT]: {
      component: (
        <ClientBodyWeightCard
          clientId={client.id}
          clientUnits={client.units}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.MEASUREMENTS]: {
      component: (
        <ClientMeasurementsCard
          clientId={client.id}
          units={client.units}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.PROGRESS_PHOTOS]: {
      component: (
        <ClientProgressPhotosCard
          clientId={client.id}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.WORKOUTS]: {
      component: (
        <ClientWorkouts
          clientId={client.id}
          username={client.username}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
    [ProfileCardType.EXERCISES]: {
      component: (
        <ClientExercises
          clientId={client.id}
          username={client.username}
          createdAt={client.created?.toISOString()}
          isPreview={isSmallScreenPreview}
        />
      ),
    },
  };

  const sortedCards = React.useMemo(() => {
    const filtered = normalizedPositions.filter((pos) => {
      if (pos.type === ProfileCardType.SPACER) return true;
      const card = components[pos.type];
      return card && card.condition !== false;
    });
    return filtered.sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });
  }, [normalizedPositions, components]);

  const firstCollapsableType = React.useMemo(() => {
    return sortedCards.find((card) => collapsableTypes.includes(card.type))
      ?.type;
  }, [sortedCards]);

  return (
    <>
      {sortedCards.map((card) => {
        if (card.type === ProfileCardType.SPACER) {
          return (
            <Grid2
              key={`spacer-${card.x}-${card.y}`}
              size={{ xs: 12, md: isSmallScreenPreview ? 12 : 6 }}
            />
          );
        }

        if (card.type === ProfileCardType.WORKOUTS_HIGHLIGHTS && !hasWorkouts) {
          return null;
        }

        const componentConfig = components[card.type];
        if (!componentConfig) return null;
        const isFirstCollapsable = card.type === firstCollapsableType;
        const shouldBeOpen = collapsable ? isFirstCollapsable : true;

        return (
          <Grid2
            key={`${card.type}-${card.x}-${card.y}`}
            size={{
              xs: 12,
              md: isSmallScreenPreview ? 12 : card.columns === 2 ? 12 : 6,
            }}
            sx={{ display: "flex" }}
          >
            {React.cloneElement(componentConfig.component, {
              sx: { flexGrow: 1, width: "100%" },
              collapsable,
              defaultOpen: shouldBeOpen,
            } as any)}
          </Grid2>
        );
      })}
    </>
  );
};
